import locationData from "./location-data.json";

const CITY_ALIASES = {
  roodeport: "roodepoort",
};

const SERVICE_PREFIXES = [
  "furniture-delivery",
  "furniture-delivery-services",
  "same-day-delivery",
  "quick-truck-hire",
  "office-removals",
  "appliance-delivery",
  "house-moves",
  "vehicle-rental",
  "moving-out",
  "bakkie-hire",
  "moving",
  "removals",
];

function normalizeCitySlug(slug) {
  let city = slug.replace(/^-+/, "");
  if (city.startsWith("ton-")) city = city.slice(4);
  return CITY_ALIASES[city] || city;
}

/** Extract the city slug from a location SEO pathname. */
export function extractCitySlug(pathname) {
  if (!pathname) return "";
  const path = pathname.startsWith("/") ? pathname : `/${pathname}`;

  const doubleDash = path.match(/--(.+)$/);
  if (doubleDash) return normalizeCitySlug(doubleDash[1]);

  const stripped = path.replace(/^\//, "");

  const tonMatch = stripped.match(/^\d+(?:\.\d+)?-ton-(.+)$/);
  if (tonMatch) return normalizeCitySlug(tonMatch[1]);

  for (const prefix of SERVICE_PREFIXES) {
    if (stripped.startsWith(`${prefix}-`)) {
      return normalizeCitySlug(stripped.slice(prefix.length + 1));
    }
  }

  const parts = stripped.split("-");
  const fallback =
    parts.length >= 2 ? parts.slice(-2).join("-") : parts[parts.length - 1];
  return normalizeCitySlug(fallback);
}

/** Services whose location-data keys use /{service}--{city}. */
export const DOUBLE_DASH_SERVICE_PREFIXES = [
  "appliance-delivery",
  "bakkie-hire",
  "courier",
  "driver-jobs",
  "fridge-delivery",
  "furniture-delivery-services",
  "house-moves",
  "last-mile-delivery",
  "mattress-delivery",
  "office-removals",
  "piano-moving",
  "student-moves",
  "truck-driver-jobs",
  "van-hire",
];

/**
 * Map a visited SEO URL to the location-data.json key that holds its copy.
 * Keeps legacy single-dash links serving the same content as canonical entries.
 */
export function resolveLocationDataPath(pathname) {
  if (!pathname) return pathname;
  if (locationData[pathname]) return pathname;

  const bakkieLegacy = pathname.match(/^\/bakkie-hire-([a-z-]+)$/);
  if (bakkieLegacy) {
    const city = normalizeCitySlug(bakkieLegacy[1]);
    const candidate = `/bakkie-hire--${city}`;
    if (locationData[candidate]) return candidate;
  }

  // Bad links from ton pages: /bakkie-hire--ton-randburg -> /bakkie-hire--randburg
  const tonCitySuffix = pathname.match(/^(\/[a-z-]+)--ton-([a-z-]+)$/);
  if (tonCitySuffix) {
    const candidate = `${tonCitySuffix[1]}--${tonCitySuffix[2]}`;
    if (locationData[candidate]) return candidate;
  }

  const furnitureLegacy = pathname.match(/^\/([a-z-]+)-furniture-transport$/);
  if (furnitureLegacy) {
    const candidate = `/furniture-delivery-${furnitureLegacy[1]}`;
    if (locationData[candidate]) return candidate;
  }

  // Chips/links use /furniture-delivery--{city}; data keys use single dash.
  const furnitureDouble = pathname.match(/^\/furniture-delivery--([a-z-]+)$/);
  if (furnitureDouble) {
    const candidate = `/furniture-delivery-${furnitureDouble[1]}`;
    if (locationData[candidate]) return candidate;
  }

  // e.g. /office-removals-centurion -> /office-removals--centurion
  if (!pathname.includes("--")) {
    for (const svc of DOUBLE_DASH_SERVICE_PREFIXES) {
      const prefix = `/${svc}-`;
      if (pathname.startsWith(prefix)) {
        const city = pathname.slice(prefix.length);
        const candidate = `/${svc}--${city}`;
        if (locationData[candidate]) return candidate;
      }
    }
  }

  return pathname;
}

export function hasLocationData(pathname) {
  const dataPath = resolveLocationDataPath(pathname);
  return Boolean(locationData[dataPath]);
}
